import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useInView from '@propertypal/shared/src/hooks/useInView';
import { Property } from '@propertypal/shared/src/types/property';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import useMediaQuery from '../media-query/useMediaQuery';
import PropertyBox from '../property-box/PropertyBox';
import PropertyPlaceholder from './PropertyPlaceholder';
import {
  Container,
  Content,
  LoadingBox,
  Rail,
  Title,
  SubTitle,
  EmptyText,
  SliderButton,
} from './SimilarProperties.style';

interface Props {
  properties?: Property[];
  onInView?: () => void;
  title?: string;
  titleSize?: number;
  subTitle?: string;
  emptyText?: string;
  background?: string;
  imageLocator?: string;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  hideOnEmpty?: boolean;
  wrapOnDesktop?: boolean;
  showArrows?: boolean;
  arrowBg?: string;
  onClick?: () => void;
  hideBadge?: boolean;
  showFooter?: boolean;
  displayPinned?: boolean;
}

const SimilarProperties: FunctionComponent<Props> = (props) => {
  const container = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(props.properties && props.properties.length > 3);
  const swiperEnabled = isMobile || !props.wrapOnDesktop; // swiper enabled prop only accepts initial prop
  useInView(container, { threshold: 0.5, rootMargin: '0px 0px 800px' }, props.onInView);

  useEffect(() => {
    swiper?.slideTo(0);
    setShowLeftArrow(false);
    setShowRightArrow(!!props.properties && props.properties.length > 3);
  }, [props.properties?.length]);

  if (props.hideOnEmpty && props.properties?.length === 0) {
    return null;
  }

  const title = () =>
    !!props.title && (
      <>
        <Title $fontSize={props.titleSize}>{props.title}</Title>

        {!!props.subTitle && <SubTitle>{props.subTitle}</SubTitle>}
      </>
    );

  if (!props.properties || (props.properties.length === 0 && props.emptyText)) {
    return (
      <Container ref={container} background={props.background}>
        <LoadingBox>
          {title()}

          {!props.properties && (
            <Rail>
              <PropertyPlaceholder hideFooter={!props.showFooter} />
              <PropertyPlaceholder hideFooter={!props.showFooter} />
              <PropertyPlaceholder />
            </Rail>
          )}

          {props.properties?.length === 0 && props.emptyText && <EmptyText>{props.emptyText}</EmptyText>}
        </LoadingBox>
      </Container>
    );
  }

  if (props.properties && props.properties.length > 0) {
    return (
      <Container data-testid="similarProperties" data-nosnippet background={props.background}>
        <Content>{title()}</Content>

        <Content $wrapOnDesktop={props.wrapOnDesktop}>
          {!swiperEnabled && (
            <Rail>
              {props.properties.map((property) => (
                <PropertyBox
                  key={property.id}
                  property={property}
                  hideFooter={!props.showFooter}
                  imageLocator={props.imageLocator}
                  onFavourite={props.onFavourite}
                  onClick={props.onClick}
                  hideBadge={props.hideBadge}
                  pinnedPropertyType={props.displayPinned && property.pinned}
                />
              ))}
            </Rail>
          )}

          {props.showArrows && (
            <>
              {showLeftArrow && (
                <SliderButton $direction="left" onClick={() => swiper?.slidePrev()} $arrowBg={props?.arrowBg}>
                  <FontAwesomeIcon icon={faChevronLeft} size={24} />
                </SliderButton>
              )}

              {showRightArrow && (
                <SliderButton $direction="right" onClick={() => swiper?.slideNext()} $arrowBg={props?.arrowBg}>
                  <FontAwesomeIcon icon={faChevronRight} size={24} />
                </SliderButton>
              )}
            </>
          )}

          {swiperEnabled && (
            <Swiper
              onSwiper={setSwiper}
              simulateTouch
              slidesPerView="auto"
              onTransitionEnd={(mSwiper) => {
                setShowLeftArrow(!mSwiper.isBeginning);
                setShowRightArrow(!mSwiper.isEnd);
              }}
            >
              {props.properties.map((property) => (
                <SwiperSlide key={property.id}>
                  <PropertyBox
                    property={property}
                    hideFooter={!props.showFooter}
                    imageLocator={props.imageLocator}
                    onFavourite={props.onFavourite}
                    onClick={props.onClick}
                    hideBadge={props.hideBadge}
                    pinnedPropertyType={props.displayPinned && property.pinned}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Content>
      </Container>
    );
  }

  return null;
};

export default SimilarProperties;
